import { jsxs, jsx } from 'react/jsx-runtime';
import { useMemo, useCallback } from 'react';
import getYear from 'date-fns/getYear';
import set from 'date-fns/set';
import { range } from 'lodash';
import MuiBox from '@mui/material/Box';
import { styled } from '@mui/material';
import dateFnsFormat from 'date-fns/format';
import '../../Pagination/Pagination.js';
import PaginationIcon from '../../Pagination/PaginationIcon.js';
import Dropdown from '../../Dropdown/Dropdown.js';
import '../../BaseDropdown/BaseDropdown.js';
import '../../BaseDropdown/types.js';
import '../../BaseDropdown/components/VirtualizedMenuList.js';
import '../../Dropdown/components/OptionItem.js';
import '../../Dropdown/components/Menu.js';
import 'tslib';
import '../../Dropdown/context/DropdownProvider.js';
import 'react-select';

const Root = styled(MuiBox) `
  display: flex;
  align-items: center;
  margin: ${({ theme }) => theme.spacing(4, 0, 0, 0)};
  justify-content: space-evenly;
`;
const IconsWrapper = styled(MuiBox) `
  display: flex;
  margin: ${({ theme }) => theme.spacing(0, 2, 0, 4)};
`;
const MonthPicker = styled(Dropdown) `
  width: 102px;
  flex: 1;
  .react-select__control {
    width: 102px;
  }
  .react-select__menu {
    width: 120px;
  }
`;
const YearPicker = styled(Dropdown) `
  width: 94px;
  ${({ theme }) => `
    margin: ${theme.spacing(0, 4, 0, 2.25)};
 `}
`;
const DatePickerHeader = ({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, maxDate, minDate, prevHint, nextHint, monthHint, yearHint, prevMonthButtonDisabled, nextMonthButtonDisabled, locale, monthFormat = 'LLLL', hideYearDropdown, }) => {
    const months = useMemo(() => {
        const rawMonths = Array(12)
            .fill(null)
            .map((_, index) => index);
        const currentYear = getYear(date);
        if (currentYear === getYear(maxDate)) {
            rawMonths.splice(maxDate.getMonth() + 1);
        }
        if (currentYear === getYear(minDate)) {
            rawMonths.splice(0, minDate.getMonth());
        }
        return rawMonths.map(value => {
            const date = set(new Date(), { month: value, date: 1 });
            const label = dateFnsFormat(date, monthFormat, { locale });
            return { value, label };
        });
    }, [date, maxDate, minDate, monthFormat, locale]);
    const years = useMemo(() => {
        return range(getYear(minDate), getYear(maxDate) + 1, 1).map(value => ({
            label: String(value),
            value,
        }));
    }, [minDate, maxDate]);
    const onChangeMonth = (option) => {
        if (option) {
            changeMonth(option.value);
        }
    };
    const onChangeYear = useCallback((option) => {
        if (option) {
            changeYear(option.value);
            const currentMonth = date.getMonth();
            if (option.value === getYear(maxDate) && maxDate.getMonth() < currentMonth) {
                changeMonth(maxDate.getMonth());
            }
            else if (option.value === getYear(minDate) && currentMonth < minDate.getMonth()) {
                changeMonth(minDate.getMonth());
            }
        }
    }, [changeYear, changeMonth, date, maxDate, minDate]);
    const yearValue = useMemo(() => {
        return years.find(year => year.value === getYear(date)) || null;
    }, [date, years]);
    const monthValue = useMemo(() => {
        return months.find(month => month.value === date.getMonth()) || null;
    }, [date, months]);
    return (jsxs(Root, Object.assign({ role: "date-picker-header", "data-testid": "date-picker-header" }, { children: [jsxs(IconsWrapper, { children: [jsx(PaginationIcon, { hint: prevHint, "data-testid": "date-picker-previous", type: "previous", onClick: decreaseMonth, disabled: prevMonthButtonDisabled }), jsx(PaginationIcon, { hint: nextHint, "data-testid": "date-picker-next", type: "next", onClick: increaseMonth, disabled: nextMonthButtonDisabled })] }), jsx(MonthPicker, { size: "large", "data-testid": "date-picker-months", hint: monthHint, value: monthValue, onChange: onChangeMonth, options: months, disableHighlighting: true }), !hideYearDropdown && (jsx(YearPicker, { size: "large", "data-testid": "date-picker-years", hint: yearHint, value: yearValue, onChange: onChangeYear, options: years, disableHighlighting: true }))] })));
};

export { DatePickerHeader as default };
//# sourceMappingURL=DatePickerHeader.js.map
